import React, { createContext, useReducer, useState, useEffect, useRef } from "react";
import { refreshSidebar } from "../services/api";
import adminStatusService from "../services/adminStatusService";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../MSL/mslConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export const AppContext = createContext();

// reducer
const reducer = (state, action) => {
  switch (action.type) {
    case "citationUrl": {
      return {
        ...state,
        citationUrl: action.payload,
      };
    }
    case "activeCitation": {
      return {
        ...state,
        activeCitation: action.payload,
      };
    }
    case "SET_AUDIO_FILE": {
      return {
        ...state,
        audioFile: action.payload,
      };
    }
    case "CLEAR_AUDIO_FILE": {
      return {
        ...state,
        audioFile: null,
      };
    }
    case "SET_AUDIO_FILE_SUMMARY": {
      return {
        ...state,
        summary: action.payload,
      };
    }
    case "AUDIO_FILE_NAME": {
      return {
        ...state,
        fileName: action.payload,
      };
    }

    case "MODAL_TOGGLE": {
      return {
        ...state,
        modalOpen: action.payload,
      };
    }

    case "SUCCESS_MODEL_OPEN": {
      return {
        ...state,
        successModal: true,
      };
    }
    case "SUCCESS_MODEL_FALSE": {
      return {
        ...state,
        successModal: false,
      };
    }
    case "hideSidebar": {
      return {
        ...state,
        hideSidebar: action.payload,
      };
    }
    case "NEW_SESSION_ID": {
      return {
        ...state,
        newSessionId: action.payload,
      };
    }
    case "DATA_DRIVEN_CHAT": {
      return {
        ...state,
        dataDrivenChat: action.payload,
      };
    }
    // Phase-3 multi-model: the three actions below do the find-and-update INSIDE
    // the reducer, against whatever state.dataDrivenChat truly is at the moment
    // each action is processed — not a snapshot the caller read earlier and
    // computed a "next array" from outside React. That distinction matters here:
    // 8 near-simultaneous per-model updates dispatched from a snapshot-based
    // read-then-write pattern race each other (each reads the same stale
    // snapshot, and whichever's derived array commits last silently discards
    // every other one's contribution, including a just-seeded new conversation).
    // A reducer never has this problem — React guarantees each dispatched action
    // sees the true previous state, applied in order, no matter how bursty.
    case "SEED_CONVERSATION": {
      return {
        ...state,
        dataDrivenChat: [action.payload, ...(state.dataDrivenChat || [])],
      };
    }
    case "APPEND_TO_CONVERSATION": {
      // payload: { session_id, messages: [...] }
      const { session_id, messages } = action.payload;
      return {
        ...state,
        dataDrivenChat: (state.dataDrivenChat || []).map((c) =>
          c?.session_id === session_id
            ? { ...c, chat_history: [...(c.chat_history || []), ...messages] }
            : c
        ),
      };
    }
    case "APPLY_MODEL_UPDATE": {
      // payload: { session_id, message_id, modelValue, patch }
      const { session_id, message_id, modelValue, patch } = action.payload;
      return {
        ...state,
        dataDrivenChat: (state.dataDrivenChat || []).map((c) => {
          if (c?.session_id !== session_id) return c;
          return {
            ...c,
            chat_history: (c.chat_history || []).map((m) => {
              if (m?.msg_id_log?.msg_id !== message_id) return m;
              const nextModelResponses = {
                ...m.modelResponses,
                [modelValue]: { ...m.modelResponses?.[modelValue], ...patch },
              };
              const nextActiveModel =
                !m.activeModel && patch.status === "done" ? modelValue : m.activeModel;
              return { ...m, modelResponses: nextModelResponses, activeModel: nextActiveModel };
            }),
          };
        }),
      };
    }
    case "MERGE_REFRESHED_CONVERSATIONS": {
      // getConvo()'s background sidebar refresh replaces the whole conversation
      // list with a fresh fetch from the backend — which only ever knows the
      // flat single-model shape, never modelResponses. A plain replace here
      // would silently wipe every conversation's multi-model dropdown data from
      // the durable store, not just the one currently on screen (a local-only
      // patch in DataDrivenChat's effect can restore it for whichever
      // conversation happens to be open at that exact moment, but does nothing
      // for any other conversation — so navigating away and back loses it
      // permanently). Instead, merge: for each freshly-fetched conversation,
      // carry over modelResponses/activeModel from the existing state for any
      // message with a matching id (or position, as a fallback), for every
      // conversation, so this survives navigating away and back.
      const freshList = action.payload || [];
      const prevBySessionId = new Map(
        (state.dataDrivenChat || []).map((c) => [c?.session_id, c])
      );
      const merged = freshList.map((freshConvo) => {
        const prevConvo = prevBySessionId.get(freshConvo?.session_id);
        if (!prevConvo?.chat_history) return freshConvo;
        const prevByMsgId = new Map(
          prevConvo.chat_history
            .filter((m) => m?.msg_id_log?.msg_id && m?.modelResponses)
            .map((m) => [m.msg_id_log.msg_id, m])
        );
        const mergedHistory = (freshConvo.chat_history || []).map((m, idx) => {
          const byId = prevByMsgId.get(m?.msg_id_log?.msg_id);
          const byPosition = prevConvo.chat_history[idx];
          const prevRich = byId?.modelResponses
            ? byId
            : byPosition?.modelResponses
            ? byPosition
            : null;
          // Backfill from the fresh backend copy (m) rather than wholesale
          // replacing it — the backend now persists modelResponses too (see
          // retrive_chat_history), so it may have a model the local copy
          // doesn't (e.g. one that finished after this browser's last update).
          return prevRich
            ? {
                ...m,
                modelResponses: { ...m.modelResponses, ...prevRich.modelResponses },
                activeModel: prevRich.activeModel || m.activeModel,
              }
            : m;
        });
        // Backend read-after-write lag (e.g. Cosmos replication) can mean a
        // message that was JUST added, and is already showing locally, isn't
        // in this fresh fetch yet — the map above only ever iterates over
        // freshConvo.chat_history, so that message would otherwise silently
        // vanish the moment this refresh lands. Union it back in rather than
        // losing it; there's no per-message delete in this app (only whole
        // conversations get soft-deleted, filtered out before this ever
        // fetches them), so a message known locally for this exact session
        // but absent from a fresh fetch is always this lag, never a real
        // deletion.
        const freshIds = new Set(
          (freshConvo.chat_history || [])
            .map((m) => m?.msg_id_log?.msg_id)
            .filter(Boolean)
        );
        // Only bot messages ever carry an id here (msg_id_log.msg_id) -- a
        // user message's id lives in its own bare message_id field instead
        // (shared with its paired bot placeholder, both set from the same
        // uuidv4() in InputFooter's handleSubmit). Checking ONLY
        // msg_id_log.msg_id above meant a user message could never match,
        // so it was never preserved by the filter below -- it silently
        // disappeared the instant this refresh landed, then only reappeared
        // once a LATER refresh found the whole turn (backend writes the user
        // message and the answer together, atomically, only once the answer
        // is ready). Same fix as DataDrivenChat.jsx's own equivalent effect:
        // find which bot turns are still missing, then keep a user message
        // only if it's paired with one of those.
        const missingBotIds = new Set(
          prevConvo.chat_history
            .filter((m) => m?.msg_id_log?.msg_id && !freshIds.has(m.msg_id_log.msg_id))
            .map((m) => m.msg_id_log.msg_id)
        );
        const missingFromFresh = prevConvo.chat_history.filter((m) => {
          const botId = m?.msg_id_log?.msg_id;
          if (botId) return missingBotIds.has(botId);
          return m?.message_id && missingBotIds.has(m.message_id);
        });
        return {
          ...freshConvo,
          chat_history: [...mergedHistory, ...missingFromFresh],
        };
      });
      return {
        ...state,
        dataDrivenChat: merged,
      };
    }
    case "DATA_PAGE_LOADER": {
      return {
        ...state,
        dataPageLoader: action.payload,
      };
    }
    case "isMultiChatRunning": {
      return {
        ...state,
        isMultiChatRunning: action.payload,
      };
    }
    case "botMessageRunning": {
      return {
        ...state,
        botMessageRunning: action.payload,
      };
    }
    case "UPDATE_MESSAGE_LIKE_STATE": {
      const { sessionId, messageId, likeState } = action.payload;
      return {
        ...state,
        dataDrivenChat: state.dataDrivenChat.map((conversation) => {
          if (conversation.session_id !== sessionId) return conversation;
          return {
            ...conversation,
            chat_history: conversation.chat_history.map((message) => {
              if (message.msg_id_log?.msg_id !== messageId) return message;
              return { ...message, like_state: likeState };
            }),
          };
        }),
      };
    }
    default: {
      return state;
      // console.log("IN DEFAULT");
    }
  }
};

const AppContextProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  // getConvo() is called from multiple places (right after sending a
  // message, on SideBar mount, after deleting a chat, etc.) and can end up
  // with several calls in flight at once — e.g. asking the same question
  // repeatedly fires one per send. Network/Cosmos read latency isn't
  // guaranteed to preserve call order, so an OLDER call can resolve AFTER a
  // NEWER one. Without a guard, MERGE_REFRESHED_CONVERSATIONS would apply
  // whichever response arrives last, even if it's the stale one — silently
  // overwriting the newer state and making the just-sent message vanish
  // until some later call happens to fix it. This sequence counter ensures
  // only the most-recently-STARTED call's result is ever applied; any
  // response for a call that's no longer the latest is discarded.
  const getConvoSeqRef = useRef(0);

  const getConvo = async () => {
    const seq = ++getConvoSeqRef.current;
    try {
      let response = await refreshSidebar();
      if (seq !== getConvoSeqRef.current) {
        // A newer getConvo() call has started since this one began —
        // this result is stale, discard it instead of overwriting newer state.
        return response;
      }
      dispatch({
        type: "MERGE_REFRESHED_CONVERSATIONS",
        payload: response?.data,
      });
      setTimeout(() => {
        dispatch({
          type: "DATA_PAGE_LOADER",
          payload: false,
        });
        dispatch({
          type: "hideSidebar",
          payload: true,
        });
      }, 0);
      return response;
    } catch (err) {
      throw err;
    }
  };
  const [messages, setMessages] = useState([]);
  const [loginLoading, setLoginLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [disclaimerModal , setDisclaimerModal] = useState(false)

  const [appData, dispatch] = useReducer(reducer, {
    activeCitation: null,
    audioFile: null,
    summary: "",
    fileName: "",
    modalOpen: false,
    successModal: false,
    hideSidebar: true,
    dataDrivenChat: [],
    newSessionId: "",
    isMultiChatRunning: false,
    botMessageRunning: false,
    getConvo,
  });

  // Initialize admin status on mount
  useEffect(() => {
    const initializeAdminStatus = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) {
        try {
          const adminStatus = await adminStatusService.getAdminStatus(userId);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error("Failed to initialize admin status:", error);
          setIsAdmin(false);
        }
      }
    };

    initializeAdminStatus();

    // Listen for admin status changes
    const unsubscribe = adminStatusService.addListener((newAdminStatus) => {
      setIsAdmin(newAdminStatus);
    });

    return unsubscribe;
  }, [setIsAdmin]);

  return (
    <AppContext.Provider
      value={{
        appData,
        dispatch,
        messages,
        setMessages,
        loginLoading,
        setLoginLoading,
        disliked,
        setDisliked,
        isAdmin,
        setIsAdmin,
        liked,
        setLiked,
        disclaimerModal , setDisclaimerModal
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
